export class WakeLockController {
  constructor({
    request,
    isVisible,
    onActiveChange = () => {},
    onError = () => {},
    setTimer = setTimeout,
    clearTimer = clearTimeout
  }) {
    this.requestSentinel = request
    this.isVisible = isVisible
    this.onActiveChange = onActiveChange
    this.onError = onError
    this.setTimer = setTimer
    this.clearTimer = clearTimer
    this.current = null
    this.pending = null
    this.generation = 0
    this.destroyed = false
    this.retryTimer = null
    this.retryAttempted = false
  }

  request() {
    if (this.destroyed || !this.requestSentinel || !this.isVisible() || this.current) {
      return Promise.resolve(this.current)
    }
    if (this.pending) return this.pending

    const generation = this.generation
    let requested
    try {
      requested = this.requestSentinel()
    } catch (error) {
      this.onError(error)
      return Promise.resolve(null)
    }

    const pending = Promise.resolve(requested)
      .then(async (sentinel) => {
        if (this.destroyed || generation !== this.generation || this.current) {
          await sentinel.release()
          return null
        }
        this.current = sentinel
        this.onActiveChange(true)
        sentinel.addEventListener('release', () => this.handleRelease(sentinel))
        return sentinel
      })
      .catch((error) => {
        if (!this.destroyed) this.onError(error)
        return null
      })
      .finally(() => {
        if (this.pending === pending) this.pending = null
      })

    this.pending = pending
    return pending
  }

  async destroy() {
    if (this.destroyed) return
    this.destroyed = true
    this.clearTimer(this.retryTimer)
    this.retryTimer = null
    await this.release()
  }

  async release() {
    this.generation += 1
    this.pending = null
    const current = this.current
    this.current = null
    if (current) {
      this.onActiveChange(false)
      await current.release()
    }
  }

  resetRetry() {
    if (!this.destroyed) this.retryAttempted = false
  }

  handleRelease(sentinel) {
    if (this.current !== sentinel) return
    this.current = null
    this.onActiveChange(false)

    if (this.destroyed || this.retryAttempted || !this.isVisible()) return
    this.retryAttempted = true
    this.retryTimer = this.setTimer(() => {
      this.retryTimer = null
      this.request()
    }, 1000)
  }
}

export class PainelAudioRuntime {
  constructor({
    audioManager,
    requestWakeLock = async () => {},
    onStateChange = () => {}
  }) {
    this.audioManager = audioManager
    this.requestWakeLock = requestWakeLock
    this.onStateChange = onStateChange
    this.ready = false
    this.restoring = false
    this.pendingAnnouncements = []
    this.flushingAnnouncements = null
    this.lastSignature = ''
    this.destroyed = false
    this.muted = false
    this.readinessGeneration = 0
  }

  get activationRequired() {
    return !this.ready && !this.muted
  }

  async restore() {
    if (this.destroyed || this.muted) return false
    const generation = this.readinessGeneration
    this.restoring = true
    const restored = await this.audioManager.restore()
    this.restoring = false
    if (this.destroyed || this.muted) return false
    if (generation !== this.readinessGeneration) return this.ready
    this.ready = Boolean(restored)
    this.notifyState()
    if (this.ready) await this.flushAnnouncements()
    return this.ready
  }

  async activate() {
    if (this.destroyed) return false
    this.readinessGeneration += 1
    const activated = await this.audioManager.activate()
    if (this.destroyed) return false
    if (!activated) {
      this.ready = false
      this.notifyState()
      return false
    }

    this.ready = true
    this.notifyState()
    await this.flushAnnouncements()
    if (this.destroyed || !this.ready) return false
    await this.requestWakeLock()
    return !this.destroyed
  }

  applyPaciente(paciente, signature, announce) {
    if (this.destroyed) return false
    if (!announce) return false

    const isNewCall = Boolean(signature) && signature !== this.lastSignature
    this.lastSignature = signature
    if (!isNewCall) return false
    if (this.muted) return false

    const announcement = { paciente, signature }

    if (this.ready && !this.flushingAnnouncements && !this.pendingAnnouncements.length) {
      this.enqueueAnnouncement(announcement)
      return true
    }

    this.retainAnnouncement(announcement)
    if (this.ready) this.flushAnnouncements()
    return false
  }

  flushAnnouncements() {
    if (this.flushingAnnouncements) return this.flushingAnnouncements

    const flush = (async () => {
      while (!this.destroyed && !this.muted && this.ready && this.pendingAnnouncements.length) {
        const announcement = this.pendingAnnouncements[0]
        if (!await this.enqueueAnnouncement(announcement)) break
        this.pendingAnnouncements.shift()
      }
    })().finally(() => {
      if (this.flushingAnnouncements === flush) this.flushingAnnouncements = null
    })

    this.flushingAnnouncements = flush
    return flush
  }

  async enqueueAnnouncement(announcement) {
    let played = false
    try {
      played = await this.audioManager.enqueue(announcement.paciente)
    } catch (error) {
      played = false
    }

    if (this.destroyed || this.muted) return false
    if (played) return true

    this.ready = false
    this.retainAnnouncement(announcement)
    this.notifyState('announcement-failed')
    return false
  }

  retainAnnouncement(announcement) {
    if (this.muted || !announcement.signature) return
    if (this.pendingAnnouncements.some(({ signature }) => signature === announcement.signature)) return
    this.pendingAnnouncements.push(announcement)
  }

  notifyState(error = '') {
    this.onStateChange({
      ready: this.ready,
      activationRequired: this.activationRequired,
      muted: this.muted,
      error
    })
  }

  mute() {
    if (this.destroyed || this.muted) return
    this.readinessGeneration += 1
    this.muted = true
    this.ready = false
    this.pendingAnnouncements = []
    this.flushingAnnouncements = null
    this.audioManager.destroy()
    this.notifyState()
  }

  destroy() {
    if (this.destroyed) return
    this.destroyed = true
    this.ready = false
    this.pendingAnnouncements = []
    this.audioManager.destroy()
  }
}
